import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  CloudRain,
  Eye,
  Flame,
  Radio,
  Shield,
  Tv,
  Waves,
  Wind,
} from "lucide-react";
import { api } from "@/lib/api";
import { tickerStyle } from "@/lib/ticker";
import { getSession } from "@/lib/auth";
import MapPanel from "@/components/MapPanel";
import Metric from "@/components/Metric";
import TrendChart from "@/components/TrendChart";
import HotspotTrend from "@/components/HotspotTrend";
import IspuTrend, { ISPU_DISTRICT_COLORS } from "@/components/IspuTrend";
import IspuDetail from "@/components/IspuDetail";
import AlertList from "@/components/AlertList";
import DistrictList from "@/components/DistrictList";

const DATE_FILTERS = [
  { value: "today_wib", label: "Hari ini" },
  { value: "today", label: "24 jam terakhir" },
  { value: "yesterday", label: "Kemarin" },
  { value: "7days", label: "7 hari" },
];

export default function Dashboard() {
  const [data, setData] = useState({
    districts: [],
    hotspots: [],
    alerts: [],
    hourly: [],
    summary: {},
    thresholds: {},
    source: "-",
  });
  const [hotspotFilter, setHotspotFilter] = useState("today_wib");
  const [confFilter, setConfFilter] = useState("all");
  const [kalbarHotspots, setKalbarHotspots] = useState([]);
  const [showIspu, setShowIspu] = useState(false);
  const [filteredHotspots, setFilteredHotspots] = useState([]);
  const [filteredSource, setFilteredSource] = useState("");
  const [reports, setReports] = useState([]);
  const [trend, setTrend] = useState([]);
  const [ispuTrend, setIspuTrend] = useState({ items: [], districts: [] });
  const [visitors, setVisitors] = useState(null);
  const [waNumber, setWaNumber] = useState("");
  const [ticker, setTicker] = useState({ text: "", enabled: false, speed: "sedang", size: "sedang" });
  const [clock, setClock] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => {
        setWaNumber(r.data.whatsapp_number || "");
        setTicker({
          text: r.data.ticker_text || "",
          enabled: Boolean(r.data.ticker_enabled),
          speed: r.data.ticker_speed || "sedang",
          size: r.data.ticker_size || "sedang",
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const tracked = sessionStorage.getItem("sitako_visit_tracked");
    const req = tracked
      ? api.get("/visitors/summary")
      : api.post("/visitors/track");
    req
      .then((r) => {
        setVisitors(r.data);
        if (!tracked) sessionStorage.setItem("sitako_visit_tracked", "1");
      })
      .catch(() => {});
  }, []);
  const [selected, setSelected] = useState("Semua wilayah");
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const session = getSession();

  useEffect(() => {
    const load = async () => {
      try {
        const [m, r, t, it] = await Promise.all([
          api.get("/monitoring"),
          api.get("/reports"),
          api.get("/hotspots/trend"),
          api.get("/ispu/trend"),
        ]);
        setData(m.data);
        setReports(r.data.items || []);
        setTrend(t.data.items || []);
        setIspuTrend(it.data || { items: [], districts: [] });
        setLastUpdated(new Date());
      } catch (e) {
        console.error(e);
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get("/hotspots", { params: { date_filter: hotspotFilter } });
        setFilteredHotspots(r.data.items || []);
        setFilteredSource(r.data.source || "");
      } catch (e) {
        console.error(e);
      }
      try {
        const k = await api.get("/hotspots/kalbar", { params: { date_filter: hotspotFilter } });
        setKalbarHotspots(k.data.items || []);
      } catch (e) {
        setKalbarHotspots([]);
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [hotspotFilter]);

  const mapHotspots = useMemo(
    () =>
      confFilter === "all"
        ? filteredHotspots
        : filteredHotspots.filter((h) => h.risk === confFilter),
    [filteredHotspots, confFilter],
  );

  const hotspotsByDistrict = useMemo(() => {
    const m = {};
    filteredHotspots.forEach((h) => {
      m[h.district] = (m[h.district] || 0) + 1;
    });
    return m;
  }, [filteredHotspots]);

  const districtsWithCounts = useMemo(
    () =>
      data.districts
        .map((d) => ({
          ...d,
          hotspot_count: hotspotsByDistrict[d.name] || 0,
        }))
        .sort((a, b) => b.hotspot_count - a.hotspot_count),
    [data.districts, hotspotsByDistrict],
  );

  const rainNowAlert = useMemo(
    () => (data.alerts || []).find((a) => a.id?.startsWith("rain-now")),
    [data.alerts],
  );

  const visibleDistricts = useMemo(
    () =>
      selected === "Semua wilayah"
        ? districtsWithCounts
        : districtsWithCounts.filter((d) => d.name === selected),
    [districtsWithCounts, selected],
  );

  const totalHotspots = filteredHotspots.length;
  const highRiskCount = useMemo(
    () => districtsWithCounts.filter((d) => d.overall_risk === "TINGGI").length,
    [districtsWithCounts],
  );

  const summary = data.summary || {};

  const shareUrl = useMemo(() => {
    const now = new Date();
    const tgl = now.toLocaleDateString("id-ID", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    const jam = now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });
    const lines = [
      "*SITAKO — Ringkasan Harian*",
      "_BPBD Kabupaten Kayong Utara_",
      `${tgl} · ${jam} WIB`,
      "",
      `🔥 Hotspot aktif: *${totalHotspots} titik*`,
      `🌧️ Hujan: ${
        summary.is_raining
          ? `rata-rata ${summary.rain_now} mm (${summary.raining_district_count} kecamatan hujan)`
          : "tidak ada hujan saat ini"
      }`,
      `🌊 Gelombang: ${summary.wave_now != null ? `${summary.wave_now} m (${summary.wave_cat || "-"})` : "data tidak tersedia"}`,
      `🌫️ ISPU udara: ${summary.ispu != null ? `*${summary.ispu}* (${summary.ispu_category})` : "data tidak tersedia"}`,
      `⚠️ Wilayah risiko tinggi: ${highRiskCount}`,
    ];
    const withFire = districtsWithCounts.filter((d) => d.hotspot_count > 0);
    if (withFire.length) {
      lines.push("", "Titik api per kecamatan:");
      withFire.forEach((d) => lines.push(`• ${d.name}: ${d.hotspot_count} titik`));
    }
    lines.push("", `Pantau selengkapnya: ${window.location.origin}`);
    return `https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`;
  }, [totalHotspots, summary, highRiskCount, districtsWithCounts]);

  return (
    <main className="app-frame">
      {ticker.enabled && ticker.text && (
        <div className="ticker-bar" data-testid="ticker-bar">
          <span className="ticker-badge">INFO</span>
          <div className="ticker-scroll">
            <p style={tickerStyle(ticker.speed, ticker.size)}>
              {ticker.text} &nbsp;&nbsp;•&nbsp;&nbsp; {ticker.text}
            </p>
          </div>
          <span className="ticker-clock" data-testid="ticker-clock">
            {clock.toLocaleTimeString("id-ID", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })}{" "}
            WIB
          </span>
        </div>
      )}
      <header className="topbar">
        <div className="brand">
          <div className="brand-mark">
            <Radio size={20} />
          </div>
          <div>
            <b title="Sistem Informasi Terpadu Antisipasi Kebencanaan Online">SITAKO</b>
            <span>SISTEM INFO TERPADU · KAYONG UTARA</span>
          </div>
        </div>
        <div className="live-state" data-testid="live-status">
          <i /> LIVE · diperbarui{" "}
          {lastUpdated.toLocaleTimeString("id-ID", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
        <a
          href={shareUrl}
          target="_blank"
          rel="noreferrer"
          className="share-wa-button"
          data-testid="share-whatsapp-button"
          title="Bagikan ringkasan kondisi hari ini ke WhatsApp"
        >
          <WhatsAppIcon /> Bagikan
        </a>
        <Link to="/tv" className="admin-button" data-testid="tv-mode-link" title="Mode layar TV Pusdalops">
          <Tv size={15} /> Mode TV
        </Link>
        {session ? (
          <Link
            to="/admin"
            className="admin-button"
            data-testid="admin-panel-link"
          >
            <Shield size={15} /> Panel Admin
          </Link>
        ) : (
          <Link
            to="/login"
            className="admin-button"
            data-testid="admin-login-button"
          >
            <Activity size={15} /> Mode publik · Login
          </Link>
        )}
      </header>

      <section className="hero">
        <div>
          <p className="eyebrow">KABUPATEN KAYONG UTARA · 24 JAM</p>
          <h1>
            Pantau perubahan,
            <br />
            <em>cegah bencana.</em>
          </h1>
          <p className="hero-copy">
            Satu ruang kendali untuk membaca titik api, hujan ekstrem, dan
            gelombang pasang di enam kecamatan.
          </p>
          <p className="hero-acronym" data-testid="sitako-acronym">
            <b>SITAKO</b> — <em>Sistem Informasi Terpadu Antisipasi Kebencanaan Online</em>,
            portal resmi BPBD Kabupaten Kayong Utara.
          </p>
        </div>
        <div className="hero-stamp">
          <span>SUMBER DATA</span>
          <strong data-testid="data-source">
            <i /> {data.source}
          </strong>
          <small>Interval sinkronisasi 60 detik</small>
        </div>
      </section>

      {rainNowAlert && (
        <div
          className={`rain-banner ${rainNowAlert.level === "TINGGI" ? "rb-high" : "rb-watch"}`}
          data-testid="rain-now-banner"
        >
          <CloudRain size={17} />
          <div>
            <b>{rainNowAlert.title} · Kec. {rainNowAlert.district}</b>
            <span>{rainNowAlert.detail}</span>
          </div>
          <em>{rainNowAlert.level}</em>
        </div>
      )}

      <section className="metrics" data-testid="summary-metrics">
        <Metric
          icon={<Flame />}
          label="Hotspot aktif"
          value={totalHotspots}
          unit=" titik"
          tone="fire"
          note={`periode: ${(DATE_FILTERS.find((f) => f.value === hotspotFilter) || {}).label || "Hari ini"} · sesuai SiPongi`}
        />
        <Metric
          icon={<CloudRain />}
          label="Rata-rata hujan"
          value={summary.is_raining ? summary.rain_now : "—"}
          unit={summary.is_raining ? " mm" : ""}
          note={
            summary.is_raining
              ? `${summary.raining_district_count} kecamatan sedang hujan`
              : "Tidak ada hujan saat ini"
          }
          tone="rain"
        />
        <Metric
          icon={<Waves />}
          label="Gelombang pasang"
          value={summary.wave_now ?? "—"}
          unit={summary.wave_now != null ? " m" : ""}
          note={
            summary.wave_now != null
              ? `${summary.wave_cat || "-"} · Perairan Kayong Utara`
              : "Data maritim tidak tersedia"
          }
          tone="wave"
        />
        <Metric
          icon={<Wind />}
          label="ISPU udara"
          value={summary.ispu ?? "—"}
          unit=""
          note={
            summary.ispu != null
              ? `${summary.ispu_category} · klik utk rincian per kecamatan`
              : "Data ISPU tidak tersedia"
          }
          tone={summary.ispu > 100 ? "alert" : "wave"}
          onClick={() => setShowIspu(true)}
        />
        <Metric
          icon={<AlertTriangle />}
          label="Risiko tinggi"
          value={highRiskCount}
          unit=" wilayah"
          tone="alert"
        />
      </section>

      {showIspu && (
        <div
          className="ispu-overlay"
          data-testid="ispu-popup"
          onClick={() => setShowIspu(false)}
        >
          <div className="ispu-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ispu-modal-head">
              <div>
                <span className="section-kicker">INDEKS STANDAR PENCEMAR UDARA</span>
                <h3>ISPU per Kecamatan · Kayong Utara</h3>
              </div>
              <button
                className="ispu-close"
                onClick={() => setShowIspu(false)}
                data-testid="ispu-popup-close"
              >
                ✕
              </button>
            </div>
            <div className="ispu-rows">
              {[...data.districts]
                .sort((a, b) => (b.ispu ?? -1) - (a.ispu ?? -1))
                .map((d) => (
                  <div
                    className="ispu-row"
                    key={d.name}
                    data-testid={`ispu-row-${d.name.toLowerCase().replaceAll(" ", "-")}`}
                  >
                    <span className="ir-name">{d.name}</span>
                    <b
                      className="ir-value"
                      style={{
                        color:
                          d.ispu_color === "#111827" ? "#94a3b8" : d.ispu_color,
                      }}
                    >
                      {d.ispu ?? "—"}
                    </b>
                    <span
                      className="ir-cat"
                      style={{
                        background: `${d.ispu_color || "#334155"}22`,
                        color:
                          d.ispu_color === "#111827" ? "#cbd5e1" : d.ispu_color,
                        borderColor: `${d.ispu_color || "#334155"}55`,
                      }}
                    >
                      {d.ispu_category || "N/A"}
                    </span>
                    <IspuSpark
                      values={ispuTrend.items.map((it) => it[d.name])}
                      color={ISPU_DISTRICT_COLORS[d.name] || "#38bdf8"}
                    />
                    <span className="ir-dom">
                      {d.ispu_dominant ? `dominan ${d.ispu_dominant}` : ""}
                    </span>
                  </div>
                ))}
            </div>
            <p className="ispu-note">
              Skala ISPU (Permen LHK P.14/2020): 0–50 Baik · 51–100 Sedang ·
              101–200 Tidak Sehat · 201–300 Sangat Tidak Sehat · &gt;300
              Berbahaya. Dihitung dari PM2.5 &amp; PM10 rata-rata 24 jam.
              Garis kecil menunjukkan tren ISPU 7 hari terakhir.
            </p>
          </div>
        </div>
      )}

      <section className="control-row">
        <div>
          <span className="section-kicker">PETA PEMANTAUAN</span>
          <h2>Titik risiko hari ini</h2>
          {filteredSource && (
            <small className="map-source" data-testid="hotspot-source">
              {filteredSource} · {mapHotspots.length} titik dipetakan
              {confFilter !== "all" &&
                ` (filter kepercayaan: ${confFilter === "TINGGI" ? "tinggi" : confFilter === "WASPADA" ? "sedang" : "rendah"})`}
            </small>
          )}
        </div>
        <div className="control-row-filters">
          <label className="select-wrap">
            <span>Periode hotspot</span>
            <select
              data-testid="hotspot-date-filter"
              value={hotspotFilter}
              onChange={(e) => setHotspotFilter(e.target.value)}
            >
              {DATE_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          {session && (
            <label className="select-wrap">
              <span>Kepercayaan</span>
              <select
                data-testid="confidence-filter"
                value={confFilter}
                onChange={(e) => setConfFilter(e.target.value)}
              >
                <option value="all">Semua tingkat</option>
                <option value="TINGGI">Tinggi</option>
                <option value="WASPADA">Sedang</option>
                <option value="RENDAH">Rendah</option>
              </select>
            </label>
          )}
          <label className="select-wrap">
            <span>Wilayah</span>
            <select
              data-testid="district-filter"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option>Semua wilayah</option>
              {data.districts.map((d) => (
                <option key={d.name}>{d.name}</option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="map-full" data-testid="map-full">
        <MapPanel
          hotspots={mapHotspots}
          kalbarHotspots={kalbarHotspots}
          districts={districtsWithCounts}
          reports={reports}
          selectedDistrict={selected}
        />
      </section>

      <DistrictList
        districts={visibleDistricts}
        selected={selected}
        onSelect={setSelected}
        onReset={() => setSelected("Semua wilayah")}
        totalHotspots={totalHotspots}
      />

      <section className="lower-grid">
        <div className="trend-panel">
          <div className="panel-heading">
            <div>
              <span className="section-kicker">TREN 24 JAM</span>
              <h3>Curah hujan & gelombang</h3>
            </div>
            <div className="chart-key">
              <span>
                <i className="rain-line" /> Hujan (mm)
              </span>
              <span>
                <i className="wave-line" /> Gelombang (m)
              </span>
            </div>
          </div>
          <TrendChart data={data.hourly} />
        </div>
        <div className="alerts-panel">
          <div className="panel-heading">
            <div>
              <span className="section-kicker">PERINGATAN TERKINI</span>
              <h3>Perlu perhatian</h3>
            </div>
            <Wind size={18} />
          </div>
          <AlertList alerts={data.alerts} />
        </div>
      </section>

      <section className="trend-duo">
        <HotspotTrend data={trend} />
        <IspuTrend data={ispuTrend.items} districts={ispuTrend.districts} />
      </section>

      <IspuDetail showAll />

      <ThresholdBand thresholds={data.thresholds} />

      <footer data-testid="app-footer">
        <div className="footer-brand">
          <img
            src="/bpbd-logo.png"
            alt="Logo BPBD Kabupaten Kayong Utara"
            data-testid="footer-bpbd-logo"
          />
          <span>
            SITAKO REALTIME <b>·</b> BPBD Kabupaten Kayong Utara
          </span>
          {waNumber && (
            <a
              className="footer-wa"
              data-testid="footer-whatsapp"
              href={`https://wa.me/${waNumber.replace(/\D/g, "").replace(/^0/, "62")}`}
              target="_blank"
              rel="noreferrer"
              title="Chat WhatsApp Layanan Informasi BPBD"
            >
              <WhatsAppIcon /> Layanan Informasi: {waNumber}
            </a>
          )}
        </div>
        <span className="footer-meta">
          <span className="footer-visitors" data-testid="visitor-count">
            <Eye size={12} />{" "}
            {visitors ? visitors.total.toLocaleString("id-ID") : "…"} pengunjung
          </span>
          <b>·</b> Koordinat WGS84 <b>·</b> Sumber: {data.source}
        </span>
      </footer>
    </main>
  );
}

function IspuSpark({ values = [], color = "#38bdf8" }) {
  const pts = values.map((v) => (v == null ? null : v));
  const nums = pts.filter((v) => v != null);
  if (nums.length < 2) return <span className="ispu-spark" />;
  const max = Math.max(...nums);
  const min = Math.min(...nums);
  const range = Math.max(max - min, 1);
  const W = 62;
  const H = 20;
  const step = W / (pts.length - 1);
  const coords = pts
    .map((v, i) => (v == null ? null : `${(i * step).toFixed(1)},${(H - 2 - ((v - min) / range) * (H - 4)).toFixed(1)}`))
    .filter(Boolean)
    .join(" ");
  return (
    <span className="ispu-spark" title="Tren ISPU 7 hari">
      <svg width={W} height={H} aria-hidden="true">
        <polyline
          points={coords}
          fill="none"
          stroke={color}
          strokeWidth="1.6"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

function WhatsAppIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}

function ThresholdBand({ thresholds = {} }) {
  const items = [
    { label: "Hujan Waspada", val: thresholds.rain_watch, unit: "mm" },
    { label: "Hujan Tinggi", val: thresholds.rain_high, unit: "mm" },
    { label: "Gelombang Waspada", val: thresholds.wave_watch, unit: "m" },
    { label: "Gelombang Tinggi", val: thresholds.wave_high, unit: "m" },
    { label: "Hotspot Waspada", val: thresholds.hotspot_watch, unit: "titik" },
    { label: "Hotspot Tinggi", val: thresholds.hotspot_high, unit: "titik" },
  ];
  return (
    <section className="threshold-band" data-testid="threshold-band">
      <div className="tb-heading">
        <span className="section-kicker">AMBANG RISIKO AKTIF</span>
        <h3>Kalibrasi indikator peringatan</h3>
      </div>
      <div className="tb-grid">
        {items.map((it) => (
          <div key={it.label} className="tb-item">
            <span>{it.label}</span>
            <strong>
              {it.val ?? "—"} <em>{it.unit}</em>
            </strong>
          </div>
        ))}
      </div>
    </section>
  );
}
